import { useMemo } from "react";
import useDatasets from "datasets/hooks/useDatasets";
import { useFetcher } from "~/hooks/useFetcher";
import { ProjectList, projectListPath } from "~/services/projects";
import { SearchHit } from "~/services/search";

/** What a hit's own listing says about it, beyond what the index returns. */
export interface HitDetails {
  /** A project's is null when left at the default. */
  accentColor: string | null;
  /** Markdown, shown when the hit has no snippet. */
  description: string | null;
}

/** A hit's details, or undefined when its type has no listing or it isn't in one. */
export type HitDetailsLookup = (hit: SearchHit) => HitDetails | undefined;

/**
 * Details for dataset and project hits, from the same dataset and project lists
 * the homepage loads, so there they cost no request. Projects past the list's
 * first page have no details.
 */
export const useHitDetails = (): HitDetailsLookup => {
  const { data: datasets } = useDatasets();
  const { data: projects } = useFetcher<ProjectList>(projectListPath());

  const byKey = useMemo(
    () =>
      new Map<string, HitDetails>([
        ...(datasets ?? []).map(
          (dataset) =>
            [
              `dataset:${dataset.id}`,
              { accentColor: dataset.accent_color, description: dataset.description || null },
            ] as const
        ),
        ...(projects?.items ?? []).map(
          (project) =>
            [
              `project:${project.id}`,
              { accentColor: project.accent_color, description: project.description },
            ] as const
        ),
      ]),
    [datasets, projects]
  );

  return (hit) => byKey.get(`${hit.entity_type}:${hit.entity_id}`);
};
